import React, { useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  Building2, 
  ArrowRight, 
  CheckCircle, 
  TrendingUp, 
  Users, 
  Clock,
  Eye,
  Play,
  MapPin,
  BarChart3,
  Home,
  Search,
  DollarSign,
  Smartphone,
  Zap
} from 'lucide-react';
import { ImageWithFallback } from '../../figma/ImageWithFallback';

const RealEstatePage: React.FC = () => {
  const containerRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: containerRef });
  const backgroundY = useTransform(scrollYProgress, [0, 1], ['0%', '50%']);
  

  const challenges = [
    'Inefficient property management and maintenance tracking across multiple locations',
    'Lack of real-time data on property performance, occupancy rates, and market trends',
    'Difficulty in providing seamless customer experiences for buyers and renters',
    'Complex coordination between agents, buyers, sellers, and property managers',
    'Time-consuming manual processes for listings, showings, and documentation',
    'Limited insights into property valuations and investment opportunities'
  ];

  const solutions = [
    {
      name: 'RealEstate Platform',
      path: '/products/real-estate',
      description: 'AI-powered platform for property management, listings, and customer engagement with advanced analytics.',
      benefits: ['Smart Listings', 'Virtual Tours', 'Market Analytics']
    },
    // {
    //   name: 'ManageRTC',
    //   path: '/products/managertc',
    //   description: 'Comprehensive workforce and property management solution for real estate operations.',
    //   benefits: ['Team Coordination', 'Task Management', 'Performance Tracking']
    // },
    // {
    //   name: 'Custom Websites',
    //   path: '/products/websites',
    //   description: 'Professional real estate websites with property showcases, virtual tours, and client portals.',
    //   benefits: ['Property Showcases', 'Lead Generation', 'Client Portals']
    // },
    // {
    //   name: 'AI-Powered Analytics',
    //   path: '/products/ai',
    //   description: 'Advanced data analytics for property valuation, market trends, and investment insights.',
    //   benefits: ['Market Insights', 'Price Prediction', 'Investment Analysis']
    // }
  ];

  const keyFeatures = [
    {
      icon: Search,
      title: 'Smart Property Search',
      description: 'AI-powered search and recommendation engine that helps clients find their perfect property.'
    },
    {
      icon: BarChart3,
      title: 'Market Intelligence',
      description: 'Real-time market analytics and insights to make data-driven investment decisions.'
    },
    {
      icon: Smartphone,
      title: 'Mobile-First Experience',
      description: 'Seamless property browsing and management from any device, anywhere.'
    },
    {
      icon: Zap,
      title: 'Automation & Efficiency',
      description: 'Automated workflows for listings, showings, documentation, and client communications.'
    }
  ];

  const benefits = [
    {
      icon: DollarSign,
      title: 'Increased Revenue',
      description: 'Drive more sales and rentals with advanced marketing tools and lead generation systems.',
      metric: '45% revenue growth'
    },
    {
      icon: Clock,
      title: 'Time Savings',
      description: 'Automate routine tasks and streamline operations to focus on high-value activities.',
      metric: '60% time reduction'
    },
    {
      icon: Users,
      title: 'Enhanced Customer Experience',
      description: 'Provide exceptional service with virtual tours, instant responses, and personalized recommendations.',
      metric: '85% satisfaction rate'
    },
    {
      icon: TrendingUp,
      title: 'Market Advantage',
      description: 'Stay ahead of competition with real-time market data and predictive analytics.',
      metric: '3x faster decisions'
    }
  ];

  return (
    <div ref={containerRef} className="relative min-h-screen pt-20">
      {/* Animated Background */}
      <motion.div
        style={{ y: backgroundY }}
        className="fixed inset-0 opacity-20"
      >
        <div className="absolute inset-0">
          {Array.from({ length: 8 }).map((_, i) => (
            <motion.div
              key={i}
              className="absolute w-4 h-4 bg-cyan-500 rounded-full"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
              }}
              animate={{
                scale: [0, 1, 0],
                opacity: [0, 1, 0],
              }}
              transition={{
                duration: 6,
                delay: i * 0.4,
                repeat: Infinity,
                repeatType: "loop"
              }}
            />
          ))}
        </div>
      </motion.div>

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            {/* Content */}
            <motion.div
              initial={{ opacity: 0, x: -100 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="flex items-center gap-3 mb-6"
              >
                <div className="p-3 rounded-xl bg-linear-to-br from-cyan-500 to-blue-600 neon-glow">
                  <Building2 className="w-8 h-8 text-white" />
                </div>
                <div>
                  <span className="text-sm font-medium text-cyan-400 uppercase tracking-wide">Smart Real Estate Technology</span>
                  <h1 className="text-4xl lg:text-6xl font-bold">
                    Real Estate <span className="bg-linear-to-r from-cyan-500 to-blue-600 bg-clip-text text-transparent">Industry</span>
                  </h1>
                </div>
              </motion.div>

              <motion.h2
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.3 }}
                className="text-2xl font-semibold mb-4 text-cyan-300"
              >
                Transforming Real Estate Through Digital Innovation
              </motion.h2>

              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.4 }}
                className="text-xl text-muted-foreground mb-8 leading-relaxed"
              >
                The real estate industry is evolving rapidly—from traditional property management to AI-driven market intelligence. 
                Digital transformation is essential for staying competitive, improving customer experiences, and maximizing returns. 
                At <span className="font-['Comfortaa'] text-cyan-400">amasQIS.ai</span>, we empower real estate professionals with cutting-edge AI, IT, and 
                IoT solutions that streamline operations, enhance decision-making, and drive growth.
              </motion.p>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.6 }}
                className="flex flex-col sm:flex-row gap-4 mb-12"
              >
                <Link
                  to="/contact"
                  className="group inline-flex items-center justify-center gap-3 bg-linear-to-r from-cyan-500 to-blue-600 px-8 py-4 rounded-xl text-white font-semibold hover:shadow-2xl hover:shadow-cyan-500/25 transition-all duration-300"
                >
                  Transform Your Business
                  <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </Link>
                <button className="inline-flex items-center justify-center gap-3 glass px-8 py-4 rounded-xl text-white font-semibold hover:glass-strong transition-all duration-300">
                  <Play className="w-5 h-5" />
                  Watch Demo
                </button>
              </motion.div>
            </motion.div>

            {/* Hero Image */}
            <motion.div
              initial={{ opacity: 0, x: 100, rotateY: -15 }}
              animate={{ opacity: 1, x: 0, rotateY: 0 }}
              transition={{ duration: 1, delay: 0.3 }}
              className="relative perspective-1000"
            >
              <div className="relative group">
                <motion.div
                  animate={{ rotate: 360 }}
                  transition={{ duration: 30, repeat: Infinity, ease: "linear" }}
                  className="absolute -inset-10 bg-linear-to-r from-cyan-500 to-blue-600 opacity-20 rounded-full blur-3xl"
                />
                
                <motion.div
                  whileHover={{ scale: 1.05, rotateY: 5, rotateX: 5 }}
                  transition={{ duration: 0.4 }}
                  className="relative z-10 glass-strong rounded-2xl overflow-hidden transform-gpu"
                >
                  <ImageWithFallback
                    src='/assets/Industries/RealEstateAI.png'
                    alt="Real Estate Industry"
                    className="w-full h-96 object-cover"
                  />
                  
                  <div className="absolute inset-0 bg-linear-to-t from-black/80 via-transparent to-transparent">
                    <div className="absolute bottom-6 left-6 right-6">
                      <div className="flex items-center gap-3 text-white">
                        <div className="p-2 bg-cyan-500/20 rounded-lg backdrop-blur-sm">
                          <Building2 className="w-5 h-5 text-cyan-500" />
                        </div>
                        <div>
                          <p className="font-semibold">AI-Powered</p>
                          <p className="text-sm text-white/80">Real Estate Solutions</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, scale: 0 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.6, delay: 1 }}
                  className="absolute -top-4 -right-4 glass p-3 rounded-xl"
                >
                  <div className="flex items-center gap-2 text-cyan-500">
                    <Home className="w-4 h-4 animate-pulse" />
                    <span className="text-xs font-mono">PROPERTY</span>
                  </div>
                </motion.div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Challenges Section */}
      <section className="relative py-15">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl lg:text-4xl font-bold mb-6">
              Challenges in the Real Estate Sector
            </h2>
            <p className="text-lg text-muted-foreground text-center max-w-3xl mx-auto">
              Real estate professionals face numerous challenges in today's fast-paced market, requiring innovative solutions for efficiency and growth.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {challenges.map((challenge, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="glass p-6 rounded-xl hover:glass-strong transition-all duration-300"
              >
                <div className="flex items-start gap-3">
                  <MapPin className="w-5 h-5 text-cyan-400 mt-0.5 shrink-0" />
                  <p className="text-muted-foreground">{challenge}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Solutions Section */}
      <section className="relative py-15">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl lg:text-4xl font-bold mb-6">
              Our Solutions for Real Estate
            </h2>
            <p className="text-lg text-muted-foreground text-center max-w-3xl mx-auto">
              Comprehensive real estate solutions designed to streamline operations, enhance customer experiences, and drive profitability.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8">
            {solutions.map((solution, index) => (
              <Link
                key={solution.name}
                to={solution.path}
                className="block group h-full"
              >
                <motion.div
                  initial={{ opacity: 0, y: 50 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.2 }}
                  viewport={{ once: true }}
                  whileHover={{ y: -5 }}
                  className="glass p-8 rounded-xl hover:glass-strong transition-all duration-300 cursor-pointer h-full flex flex-col"
                >
                  <div className="flex items-start justify-between mb-3">
                    <h3 className="text-xl font-semibold text-cyan-400 group-hover:text-cyan-300 transition-colors">
                      {solution.name}
                    </h3>
                    <ArrowRight className="w-5 h-5 text-cyan-400 group-hover:translate-x-1 transition-transform" />
                  </div>
                  <p className="text-muted-foreground mb-4">{solution.description}</p>
                  <div className="space-y-2">
                    {solution.benefits.map((benefit) => (
                      <div key={benefit} className="flex items-center gap-2">
                        <CheckCircle className="w-4 h-4 text-cyan-400 shrink-0" />
                        <span className="text-sm text-white/90">{benefit}</span>
                      </div>
                    ))}
                  </div>
                </motion.div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Key Features Section */}
      <section className="relative py-15">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl lg:text-4xl font-bold mb-6">
              Key Features for Real Estate Excellence
            </h2>
            <p className="text-lg text-muted-foreground text-center max-w-3xl mx-auto">
              Our platform is built with the unique needs of real estate professionals in mind.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {keyFeatures.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <motion.div
                  key={feature.title}
                  initial={{ opacity: 0, y: 50 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="glass p-6 rounded-xl hover:glass-strong transition-all duration-300 text-center"
                >
                  <div className="inline-flex items-center justify-center w-14 h-14 rounded-xl bg-linear-to-br from-cyan-500/20 to-blue-500/20 mb-4">
                    <Icon className="w-7 h-7 text-cyan-400" />
                  </div>
                  <h3 className="font-semibold mb-2">{feature.title}</h3>
                  <p className="text-sm text-muted-foreground">{feature.description}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="relative py-15">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl lg:text-4xl font-bold mb-6">
              Measurable Business Impact
            </h2>
            <p className="text-lg text-muted-foreground text-center max-w-3xl mx-auto">
              Real results from real estate professionals who transformed their operations with our solutions.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {benefits.map((benefit, index) => {
              const Icon = benefit.icon;
              return (
                <motion.div
                  key={benefit.title}
                  initial={{ opacity: 0, y: 50 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.15 }}
                  viewport={{ once: true }}
                  className="glass p-8 rounded-xl hover:glass-strong transition-all duration-300 cursor-pointer h-full flex flex-col"
                >
                  <div className="inline-flex items-center justify-center w-16 h-16 rounded-xl bg-linear-to-br from-cyan-500/20 to-blue-500/20 mb-6">
                    <Icon className="w-8 h-8 text-cyan-400" />
                  </div>
                  <h3 className="text-xl font-semibold mb-3">{benefit.title}</h3>
                  <p className="text-muted-foreground mb-4">{benefit.description}</p>
                  <div className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-cyan-500/10 border border-cyan-500/20">
                    <TrendingUp className="w-4 h-4 text-cyan-400" />
                    <span className="text-sm font-semibold text-cyan-400">{benefit.metric}</span>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative py-15">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="glass-strong rounded-2xl p-12 text-center"
          >
            <div className="max-w-3xl mx-auto">
              <h2 className="text-3xl lg:text-4xl font-bold mb-6">
                Ready to Transform Your Real Estate Business?
              </h2>
              <p className="text-lg text-muted-foreground mb-8">
                Join the leading real estate professionals who are leveraging AI and automation to stay ahead of the competition.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link
                  to="/contact"
                  className="group inline-flex items-center justify-center gap-3 bg-linear-to-r from-cyan-500 to-blue-600 px-8 py-4 rounded-xl text-white font-semibold hover:shadow-2xl hover:shadow-cyan-500/25 transition-all duration-300"
                >
                  Get Started Today
                  <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </Link>
                <Link
                  to="/products/realestate"
                  className="inline-flex items-center justify-center gap-3 glass px-8 py-4 rounded-xl text-white font-semibold hover:glass-strong transition-all duration-300"
                >
                  <Eye className="w-5 h-5" />
                  Explore Solutions
                </Link>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="relative py-15">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-8">
            {[
              { value: '500+', label: 'Properties Managed', icon: Building2 },
              { value: '98%', label: 'Client Satisfaction', icon: Users },
              { value: '60%', label: 'Time Saved', icon: Clock },
              { value: '45%', label: 'Revenue Growth', icon: DollarSign }
            ].map((stat, index) => {
              const Icon = stat.icon;
              return (
                <motion.div
                  key={stat.label}
                  initial={{ opacity: 0, scale: 0.8 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="glass p-6 rounded-xl text-center"
                >
                  <Icon className="w-8 h-8 text-cyan-400 mx-auto mb-3" />
                  <div className="text-3xl font-bold text-cyan-400 mb-2">{stat.value}</div>
                  <div className="text-sm text-muted-foreground">{stat.label}</div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>
    </div>
  );
};

export default RealEstatePage;
